import { moneySaid } from "@rally/agreed";
import {
  Button,
  Col,
  ScrollArea,
  type ScrollHandle,
  Tappable,
  Text,
  useFocusRing,
  useTheme,
} from "@rally/ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { stopLabel } from "../../localization/labels.ts";
import { say } from "../../localization/say.ts";
import type { Turn } from "../activity.ts";
import { useNow } from "./tool-frame.tsx";
import {
  filteredEvents,
  heartbeatAge,
  type InspectorEvent,
  type InspectorFilter,
  inspectorEvents,
} from "./turn-inspector-model.ts";
import { requestPresentation, turnCostPresentation } from "./turn-request.ts";

const TOOL_LIMIT = 200;
const NEAR_LIMIT = 180;
const PAGE = 30;

function duration(ms: number | null | undefined): string {
  const seconds = Math.max(0, Math.floor((ms ?? 0) / 1000));
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}:${String(seconds % 60).padStart(2, "0")}` : `${seconds}s`;
}

function stateSaid(turn: Turn): string {
  if (turn.state === "started") return say("agent.activeState");
  if (turn.state === "waiting") return say("agent.waitingState");
  if (turn.state === "finished") return say("agent.finishedState");
  const stopped = turn.steps?.find((step) => step.kind === "run.stopped");
  return typeof stopped?.payload.how === "string"
    ? stopLabel(stopped.payload.how)
    : say("agent.stoppedState");
}

function delegationStateSaid(state: unknown): string {
  if (state === "started") return say("agent.delegationState.started");
  if (state === "waiting") return say("agent.delegationState.waiting");
  if (state === "finished") return say("agent.delegationState.finished");
  return say("agent.delegationState.stopped");
}

function filterSaid(filter: InspectorFilter): string {
  if (filter === "messages") return say("agent.filter.messages");
  if (filter === "tools") return say("agent.filter.tools");
  if (filter === "delegations") return say("agent.filter.delegations");
  if (filter === "system") return say("agent.filter.system");
  return say("agent.filter.all");
}

function eventSaid(event: InspectorEvent): string | null {
  const tool = typeof event.payload.tool === "string" ? event.payload.tool : say("agent.aTool");
  const object = typeof event.payload.object === "string" ? ` · ${event.payload.object}` : "";
  if (event.kind === "run.message") return String(event.payload.text ?? "");
  if (event.kind === "run.tool.started") return say("agent.toolStarted")(event.ordinal ?? 0, tool, object);
  if (event.kind === "run.tool.finished")
    return say("agent.toolResult")(event.ordinal ?? 0, tool, event.payload.ok === true);
  if (event.kind === "run.delegation")
    return say("agent.delegated")(delegationStateSaid(event.payload.state));
  if (event.kind === "run.retry") return say("agent.retry")(Number(event.payload.attempt ?? 0));
  if (event.kind === "run.compaction.started") return say("agent.compactionStarted");
  if (event.kind === "run.compaction.finished") return say("agent.compactionFinished");
  if (event.kind === "run.usage")
    return say("agent.usage")(
      Number(event.payload.tokens ?? 0),
      moneySaid(Number(event.payload.amount ?? 0)),
    );
  if (event.kind === "run.telemetry.dropped")
    return say("agent.telemetryDropped")(Number(event.payload.count ?? 0));
  if (event.kind === "run.started") return say("agent.progressStarted");
  if (event.kind === "run.finished") return say("agent.finishedState");
  if (event.kind === "run.stopped") return say("agent.stoppedState");
  return null;
}

function Disclosure({
  label,
  expanded,
  onPress,
  children,
}: {
  label: string;
  expanded: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) {
  const focus = useFocusRing();
  return (
    <Tappable
      {...focus.focusProps}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ expanded }}
      onPress={onPress}
      style={focus.ring()}
    >
      {children}
    </Tappable>
  );
}

function EventRow({ event }: { event: InspectorEvent }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const label = eventSaid(event);
  if (!label) return null;
  return (
    <Col style={{ paddingVertical: theme.space[1] }} testID={`agent-event-${event.category}`}>
      <Disclosure label={label} expanded={expanded} onPress={() => setExpanded((value) => !value)}>
        <Text typeName="caption" color="text.secondary">
          {label}
        </Text>
      </Disclosure>
      {expanded ? (
        <Col style={{ paddingLeft: theme.space[2], gap: theme.space[1] }}>
          {event.kind === "run.tool.finished" ? (
            <Text typeName="caption" color="text.tertiary">
              {say("agent.safeToolMetadata")(
                event.ordinal ?? 0,
                String(event.payload.tool ?? say("agent.aTool")),
                event.payload.ok === true,
                typeof event.payload.durationMs === "number" ? duration(event.payload.durationMs) : "",
              )}
            </Text>
          ) : event.kind === "run.delegation" ? (
            <Text typeName="caption" color="text.tertiary">
              {say("agent.safeDelegationMetadata")(
                delegationStateSaid(event.payload.state),
                Number(event.payload.toolCount ?? 0),
                event.payload.lastActivityAt
                  ? new Date(String(event.payload.lastActivityAt)).toLocaleTimeString()
                  : say("agent.heartbeatPending"),
              )}
            </Text>
          ) : (
            <Text typeName="caption" color="text.tertiary">
              {new Date(event.at).toLocaleTimeString()}
            </Text>
          )}
        </Col>
      ) : null}
    </Col>
  );
}

function CurrentActivity({ turn, now }: { turn: Turn; now: number }) {
  const child = turn.delegations?.find((one) => one.state === "started" || one.state === "waiting");
  const activity = child
    ? say("agent.waitingForChild")
    : turn.currentTool
      ? say("agent.usingTarget")(turn.currentTool.tool, turn.currentTool.object ?? "")
      : turn.state === "waiting"
        ? say("agent.waitingState")
        : say("agent.processingRequest");
  const since = child?.startedAt ?? turn.currentTool?.startedAt ?? turn.startedAt;
  const age = heartbeatAge(child?.lastActivityAt ?? turn.heartbeatAt, now);
  return (
    <Col style={{ gap: 2 }} testID="agent-current-activity">
      <Text typeName="label" color="text.primary">
        {say("agent.currentActivity")}
      </Text>
      <Text typeName="caption" color="text.secondary">
        {activity}
        {since ? ` · ${duration(now - Date.parse(since))}` : ""}
      </Text>
      <Text typeName="caption" color="text.tertiary">
        {age === null ? say("agent.heartbeatPending") : say("agent.heartbeatAge")(duration(age))}
      </Text>
    </Col>
  );
}

export function TurnDetail({ turn, requestedBy = null }: { turn: Turn; requestedBy?: string | null }) {
  const theme = useTheme();
  const active = turn.state === "started" || turn.state === "waiting";
  const [expanded, setExpanded] = useState(active);
  const [filter, setFilter] = useState<InspectorFilter>("all");
  const [shown, setShown] = useState(PAGE);
  const [movedUp, setMovedUp] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const timeline = useRef<ScrollHandle | null>(null);
  const now = useNow(active);
  const allEvents = useMemo(() => inspectorEvents(turn), [turn]);
  const filtered = filteredEvents(allEvents, filter);
  const visible = filtered.slice(-shown);
  const previousCount = useRef(allEvents.length);
  useEffect(() => {
    const added = Math.max(0, allEvents.length - previousCount.current);
    if (movedUp && added) setNewCount((count) => count + added);
    previousCount.current = allEvents.length;
  }, [allEvents.length, movedUp]);

  const durationMs = active && turn.startedAt ? now - Date.parse(turn.startedAt) : turn.durationMs;
  const status = stateSaid(turn);
  const request = requestPresentation(turn.request, requestedBy, expanded);
  const stoppedAtLimit =
    turn.state === "stopped" &&
    turn.steps?.some((step) => step.kind === "run.stopped" && step.payload.how === "tooled");

  return (
    <Col
      testID="agent-turn-card"
      style={{
        gap: theme.space[2],
        borderWidth: 1,
        borderColor: active ? theme.color.accent : theme.color["border.default"],
        borderRadius: theme.radius.lg,
        backgroundColor: theme.color["bg.surface"],
        padding: theme.space[3],
      }}
    >
      <Disclosure
        label={say("agent.turnDisclosure")(turn.turn, expanded)}
        expanded={expanded}
        onPress={() => setExpanded((value) => !value)}
      >
        <Text typeName="label" color="text.primary">
          {active ? say("agent.liveTurn")(turn.turn) : say("agent.turn")(turn.turn)}
        </Text>
        <Text typeName="caption" color="text.secondary">
          {status} · {duration(durationMs)} · {turnCostPresentation(turn, active)}
        </Text>
        {!expanded && request ? (
          <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
            {request.text}
          </Text>
        ) : null}
      </Disclosure>

      {expanded ? (
        <Col style={{ gap: theme.space[3] }} testID="agent-turn-inspector">
          <Col style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space[2] }}>
            <Text typeName="caption" color="text.secondary">
              {say("agent.toolsMetric")(turn.toolCount ?? 0, TOOL_LIMIT)}
            </Text>
            <Text typeName="caption" color="text.secondary">
              {say("agent.compactionsMetric")(turn.compactions ?? 0)}
            </Text>
            <Text typeName="caption" color="text.secondary">
              {say("agent.retriesMetric")(turn.retries ?? 0)}
            </Text>
          </Col>
          <Col accessibilityLabel={say("agent.toolLimitAria")(turn.toolCount ?? 0, TOOL_LIMIT)}>
            <Col style={{ height: 5, borderRadius: 3, backgroundColor: theme.color["border.default"] }}>
              <Col
                style={{
                  height: 5,
                  borderRadius: 3,
                  width: `${Math.min(100, ((turn.toolCount ?? 0) / TOOL_LIMIT) * 100)}%`,
                  backgroundColor:
                    (turn.toolCount ?? 0) >= NEAR_LIMIT ? theme.color.warning : theme.color.accent,
                }}
              />
            </Col>
            {(turn.toolCount ?? 0) >= NEAR_LIMIT ? (
              <Text
                typeName="caption"
                color={(turn.toolCount ?? 0) >= TOOL_LIMIT ? "danger" : "warning"}
                accessibilityLiveRegion="polite"
              >
                {(turn.toolCount ?? 0) >= TOOL_LIMIT
                  ? say("agent.toolLimitStopped")
                  : say("agent.toolLimitWarning")(TOOL_LIMIT - (turn.toolCount ?? 0))}
              </Text>
            ) : null}
          </Col>
          {request ? (
            <Col style={{ gap: 2 }} testID="agent-linked-request">
              <Text typeName="caption" color="text.tertiary">
                {say("agent.linkedRequest")} · {new Date(turn.request?.at ?? "").toLocaleTimeString()}
              </Text>
              <Text typeName="caption" color="text.primary" numberOfLines={request.numberOfLines}>
                “{request.text}”
              </Text>
            </Col>
          ) : null}
          {active ? <CurrentActivity turn={turn} now={now} /> : null}
          {stoppedAtLimit ? (
            <Text typeName="caption" color="danger">
              {say("agent.toolLimitStopped")}
            </Text>
          ) : null}
          <Col gap={2}>
            <Text typeName="label" color="text.primary">
              {say("agent.progress")}
            </Text>
            <Text typeName="caption" color="text.tertiary">
              {say("agent.privacyNote")}
            </Text>
            <Col
              accessibilityRole="tablist"
              style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space[1] }}
            >
              {(["all", "messages", "tools", "delegations", "system"] as InspectorFilter[]).map((one) => (
                <Button
                  key={one}
                  size="sm"
                  accessibilityRole="tab"
                  accessibilityState={{ selected: filter === one }}
                  aria-pressed={filter === one}
                  variant={filter === one ? "primary" : "outline"}
                  onPress={() => {
                    setFilter(one);
                    setShown(PAGE);
                  }}
                  testID={`agent-filter-${one}`}
                >
                  {filterSaid(one)}
                </Button>
              ))}
            </Col>
            {filtered.length > shown ? (
              <Button size="sm" variant="ghost" onPress={() => setShown((count) => count + PAGE)}>
                {say("agent.showEarlier")(Math.min(PAGE, filtered.length - shown))}
              </Button>
            ) : null}
            <ScrollArea
              ref={timeline}
              style={{ maxHeight: 280 }}
              onScroll={({ nativeEvent }) =>
                setMovedUp(
                  nativeEvent.contentOffset.y + nativeEvent.layoutMeasurement.height <
                    nativeEvent.contentSize.height - 24,
                )
              }
              scrollEventThrottle={100}
              onContentSizeChange={() => {
                if (!movedUp) timeline.current?.scrollToEnd({ animated: false });
              }}
            >
              {visible.map((event) => (
                <EventRow key={event.key} event={event} />
              ))}
            </ScrollArea>
            {newCount > 0 ? (
              <Button
                size="sm"
                variant="outline"
                onPress={() => {
                  setMovedUp(false);
                  setNewCount(0);
                  timeline.current?.scrollToEnd({ animated: false });
                }}
                testID="agent-new-events"
              >
                {say("agent.newEvents")(newCount)}
              </Button>
            ) : null}
          </Col>
        </Col>
      ) : null}
    </Col>
  );
}
